import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

export const AdminMembers = () => {
  const [data, setData] = useState({ members: [], newsletter: [], broadcasts: [], sendgrid_enabled: false, whatsapp_enabled: false, whatsapp_count: 0 });
  const [sending, setSending] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [compose, setCompose] = useState({ subject: "", message: "", include_hero: true });

  const load = () => api.get("/admin/members").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const broadcast = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/newsletter/broadcast", compose);
      toast.success(res.message);
      setCompose({ subject: "", message: "", include_hero: true });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const waBroadcast = async () => {
    setWaSending(true);
    try {
      const { data: res } = await api.post("/admin/whatsapp/broadcast");
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWaSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-10">
      <div>
        <h2 className="font-display text-2xl mb-4">MEMBERS ({data.members.length})</h2>
        <div className="border border-[#27272A] overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[480px]" data-testid="admin-members-table">
            <thead>
              <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-[#27272A]">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-b border-[#27272A] last:border-0 hover:bg-[#111111]">
                  <td className="px-4 py-3">{m.name}</td>
                  <td className="px-4 py-3 text-white/60">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest ${m.role === "admin" ? "text-[#FF3B30]" : "text-white/40"}`}>{m.role}</span>
                  </td>
                  <td className="px-4 py-3 text-white/40 text-[11px]">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="space-y-8">
        <div className="border-2 border-[#FFCC00] p-6">
          <h2 className="font-display text-2xl mb-2">NEWSLETTER BROADCAST</h2>
          <p className="text-white/50 text-xs mb-4">Compose and send to all {data.newsletter.length} subscriber(s) direct from here via SendGrid.</p>
          {!data.sendgrid_enabled && (
            <p className="text-[#FF3B30] text-xs mb-4 border border-[#FF3B30] p-3" data-testid="broadcast-not-configured">
              Needs your SendGrid API key + verified sender email in backend/.env (SENDGRID_API_KEY, SENDER_EMAIL) — get one at sendgrid.com.
            </p>
          )}
          <div className="space-y-3 mb-4">
            <input
              data-testid="broadcast-subject-input"
              placeholder="SUBJECT (BLANK = AUTO HERO DEAL SUBJECT)"
              value={compose.subject}
              onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
              className="w-full bg-transparent border border-[#27272A] px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30"
            />
            <textarea
              data-testid="broadcast-message-input"
              rows={3}
              placeholder="OPTIONAL MESSAGE TO SUBSCRIBERS…"
              value={compose.message}
              onChange={(e) => setCompose({ ...compose, message: e.target.value })}
              className="w-full bg-transparent border border-[#27272A] px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30"
            />
            <label className="flex items-center gap-3 text-xs text-white/70">
              <input data-testid="broadcast-include-hero" type="checkbox" checked={compose.include_hero} onChange={(e) => setCompose({ ...compose, include_hero: e.target.checked })} className="accent-[#FFCC00]" />
              INCLUDE TODAY'S HERO DEAL BLOCK
            </label>
          </div>
          <button
            data-testid="send-broadcast-button"
            onClick={broadcast}
            disabled={sending || !data.sendgrid_enabled}
            className="btn-industrial w-full py-3 text-lg flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <Send size={16} /> {sending ? "SENDING…" : "SEND BROADCAST"}
          </button>
          {data.broadcasts.length > 0 && (
            <div className="mt-4 text-[11px] text-white/40 space-y-1" data-testid="broadcast-history">
              {data.broadcasts.slice(0, 3).map((b) => (
                <div key={b.id}>✓ {b.deal_title} → {b.recipients} recipients · {new Date(b.sent_at).toLocaleString("en-GB")}</div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-[#27272A] p-6">
          <h2 className="font-display text-2xl mb-2">WHATSAPP BROADCAST</h2>
          <p className="text-white/50 text-xs mb-4">Blast today's hero deal to {data.whatsapp_count} opted-in WhatsApp subscriber(s) via Twilio.</p>
          {!data.whatsapp_enabled && (
            <p className="text-[#FF3B30] text-xs mb-4 border border-[#FF3B30] p-3" data-testid="whatsapp-not-configured">
              Needs your Twilio credentials in backend/.env (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM) — get them at twilio.com.
            </p>
          )}
          <button
            data-testid="send-whatsapp-broadcast-button"
            onClick={waBroadcast}
            disabled={waSending || !data.whatsapp_enabled}
            className="btn-ghost-industrial w-full py-3 text-lg disabled:opacity-40"
          >
            {waSending ? "SENDING…" : "SEND WHATSAPP BLAST"}
          </button>
        </div>
        <div>
          <h2 className="font-display text-2xl mb-4">NEWSLETTER ({data.newsletter.length})</h2>
          <div className="border border-[#27272A] max-h-[360px] overflow-y-auto" data-testid="admin-newsletter-list">
            {data.newsletter.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-[#27272A] last:border-0 text-sm text-white/60">{n.email}</div>
            ))}
            {data.newsletter.length === 0 && <div className="px-4 py-8 text-center text-white/30 text-sm">No signups yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
